import { gql, useQuery } from '@apollo/client';
import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import { DateTime } from 'luxon';
import { T, always, cond, equals, includes } from 'ramda';
import React, { FC } from 'react';
import { useAsync } from 'react-use';

import { keysToCamel } from '@utils/butter';
import { useRedirectQuery } from '@utils/redirectQuery';

import { useAuth } from '@providers/AuthProvider';

import { IScheduledAppointment, IScheduledAppointments } from '@folx-types/scheduledAppointments';

import SCHEDULED_APPOINTMENTS_QUERY from '@graphql/queries/currentMemberSchedulingInfo.gql';

import ManageAppointments from './ManageAppointments.component';

export interface IManageAppointmentsContent {
	butterPage: {
		confirmerrortext: string;
		iframeLoadingDisclaimer: string;
	};
}

// list of subscriptions that have options for brief and extended visits
const SubsWithVisitDurationOptions = ['HRT_T', 'HRT_E'];
// list of subscriptions that always have a 'TRANSACTIONAL' status, not 'ACTIVE', etc
const transactionalSubscriptions = ['VC', 'MHT', 'MHM'];

const MEMBER_QUERY = gql`
	query member($memberId: UUID!) {
		member(id: $memberId) {
			affirmingFirstName
			affirmingLastName
			legalFirstName
			legalLastName
			email
			stateOfResidence
			athenaMemberMaps {
				nodes {
					athenaId
				}
			}
			memberSubscriptions(condition: { subscriptionStatus: "ACTIVE" }) {
				nodes {
					subscriptionType
					requiresAvVisit
				}
			}
		}
	}
`;

export type ActionChoice =
	| {
			action: 'schedule';
			serviceType: 't';
			duration: 'brief' | 'extended';
			version: 2;
	  }
	| {
			action: 'schedule';
			serviceType: 'e';
			duration: 'brief' | 'extended';
			version: 2;
	  }
	| {
			action: 'schedule';
			serviceType: 'ed' | 'prep' | 'inj';
			duration: undefined;
			version: 1;
	  }
	| {
			action: 'reschedule';
			serviceType: 'vc' | 't' | 'e' | 'prep' | 'ed' | 'inj' | 'mht' | 'mhm';
			cogsworthUrl: string;
			scheduledTime: DateTime;
			duration: undefined;
	  };

interface IMember {
	member: {
		affirmingFirstName: string;
		affirmingLastName: string;
		legalFirstName: string;
		legalLastName: string;
		email: string;
		stateOfResidence: string;
		athenaMemberMaps: {
			nodes: [
				{
					athenaId: number;
				}
			];
		};
		memberSubscriptions: { nodes: IMemberSubscription[] };
	};
}

interface IMemberSubscription {
	subscriptionType: string;
	requiresAvVisit: boolean;
}

interface IAppointmentCalculationValue {
	actionChoices?: ActionChoice[];
	confirmErrorRescheduleUrl?: string;
}

export const serviceDisplayName = (
	serviceType: 'e' | 't' | 'prep' | 'vc' | 'ed' | 'inj' | 'mht' | 'mhm'
): { article: string; name: string } => {
	switch (serviceType) {
		case 'e': {
			return { article: 'an', name: 'Estrogen HRT' };
		}
		case 't': {
			return { article: 'a', name: 'Testosterone HRT' };
		}
		case 'prep': {
			return { article: 'a', name: 'PrEP' };
		}
		case 'vc': {
			return { article: 'a', name: 'Virtual Healthcare' };
		}
		case 'ed': {
			return { article: 'an', name: 'ED' };
		}
		case 'mht': {
			return { article: 'a', name: 'Therapy' };
		}
		case 'mhm': {
			return { article: 'a', name: 'Medication' };
		}
		default: {
			return { article: '', name: '' };
		}
	}
};

const ManageAppointmentsPage: FC<{}> = () => {
	const {
		loading: loadingSchedulingData,
		error: errorSchedulingData,
		data: schedulingData,
	} = useQuery<IScheduledAppointments>(SCHEDULED_APPOINTMENTS_QUERY);

	const { userId, getAuthToken } = useAuth();
	const location = useLocation();

	const {
		loading: loadingMemberData,
		error: errorMemberData,
		data: memberData,
	} = useQuery<IMember, { memberId: string }>(MEMBER_QUERY, {
		variables: {
			memberId: userId,
		},
	});

	const pageData = useStaticQuery<Record<string, string>>(
		graphql`
			{
				butterPage(slug: { eq: "manage-appointments" }) {
					confirmerrortext
					iframe_loading_disclaimer
				}
			}
		`
	);

	const subscriptions = memberData?.member.memberSubscriptions.nodes || [];

	const upcomingAppointments = schedulingData?.vMemberSchedulingInfoForCurrentMember?.nodes.filter(
		(node: any) => node.upcoming && !node.canceledDt
	);

	const getServiceToken = cond([
		[equals('HRT_T'), always('t' as const)],
		[equals('HRT_E'), always('e' as const)],
		[equals('ED'), always('ed' as const)],
		[equals('PREP'), always('prep' as const)],
		[equals('VC'), always('vc' as const)],
		[equals('INJ'), always('inj' as const)],
		[equals('MHT'), always('mht' as const)],
		[equals('MHM'), always('mhm' as const)],

		[T, (service) => service as 'vc' | 't' | 'e' | 'prep' | 'ed' | 'inj' | 'mht' | 'mhm'],
	]);

	const memberState = memberData?.member?.stateOfResidence?.toLowerCase() ?? null;

	const redirectQuery = useRedirectQuery(location); // exists if coming back with error from apppointment_managed page
	const confirmError = redirectQuery?.confirmError === 'true';

	const { value, loading } = useAsync<() => Promise<IAppointmentCalculationValue>>(async () => {
		let confirmErrorRescheduleUrl: string | undefined;
		if (confirmError) {
			const rescheduleAppt = upcomingAppointments?.find(
				(appt) => appt.cogsworthId == redirectQuery?.cogsworthId
			);
			confirmErrorRescheduleUrl = rescheduleAppt?.rescheduleLink || undefined;
		}

		let actionChoices: ActionChoice[] = [];

		// Show the option to reschedule all upcoming appointments in all services
		for (const appt of upcomingAppointments!) {
			actionChoices = addRescheduleChoice(actionChoices, appt);
		}

		// For subscription services (old membership), show the option to schedule a new appointment unless there's already one scheduled
		for (const s of subscriptions.filter(
			({ subscriptionType }) => subscriptionType != 'MEMBERSHIP'
		)) {
			const upcomingSubscriptionAppointment = upcomingAppointments?.find(
				(apt: IScheduledAppointment) =>
					getServiceToken(apt.serviceInfo.type) == getServiceToken(s.subscriptionType)
			);
			console.log(
				`found upcoming appt for a subscription: ${JSON.stringify(upcomingSubscriptionAppointment)}`
			);

			if (!upcomingSubscriptionAppointment) {
				if (includes(s.subscriptionType, SubsWithVisitDurationOptions)) {
					actionChoices = addScheduleNewChoice(actionChoices, s.subscriptionType, 'BRIEF');
					actionChoices = addScheduleNewChoice(actionChoices, s.subscriptionType, 'EXTENDED');
				} else {
					actionChoices = addScheduleNewChoice(actionChoices, s.subscriptionType);
				}
			}
		}

		console.log(`choices generated: ${JSON.stringify(actionChoices)}`);

		return { actionChoices, confirmErrorRescheduleUrl };
	}, [memberData, schedulingData]);

	const { actionChoices, confirmErrorRescheduleUrl } = value || {};

	const addScheduleNewChoice = (
		previousChoices: ActionChoice[],
		subscriptionType: string,
		duration?: string
	): ActionChoice[] => {
		const newChoice = {
			action: 'schedule' as const,
			serviceType: getServiceToken(subscriptionType),
			duration: duration?.toLowerCase() || undefined,
			version: subscriptionType == 'HRT_E' || subscriptionType == 'HRT_T' ? 2 : 1,
		} as ActionChoice;
		const combined = actionChoices?.concat([newChoice]);
		console.log(`new action choices array from addScheduleNewChoice: ${JSON.stringify(combined)}`);
		const updatedChoices = previousChoices.concat([newChoice]);
		return updatedChoices;
	};

	const addRescheduleChoice = (
		previousChoices: ActionChoice[],
		appointment: IScheduledAppointment
	): ActionChoice[] => {
		const newChoice = {
			action: 'reschedule' as const,
			serviceType: getServiceToken(appointment.serviceInfo.type),
			cogsworthUrl: appointment.rescheduleLink,
			scheduledTime: DateTime.fromISO(appointment.scheduledDt),
		} as ActionChoice;
		const updatedChoices = previousChoices.concat([newChoice]);
		console.log(
			`new action choices array from addRescheduleChoice: ${JSON.stringify(updatedChoices)}`
		);
		return updatedChoices;
	};

	const pageContent = keysToCamel(pageData) as IManageAppointmentsContent;

	return (
		<ManageAppointments
			loadingData={loadingSchedulingData || loadingMemberData || loading}
			confirmError={confirmError}
			confirmErrorRescheduleUrl={confirmErrorRescheduleUrl}
			pageContent={pageContent}
			memberState={memberState}
			actionChoices={actionChoices}
			serviceDisplayName={serviceDisplayName}
		/>
	);
};

export default ManageAppointmentsPage;
